import React, { useState } from 'react';
import { format } from 'date-fns';
import { Edit, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import InvoiceStatusBadge from '../InvoiceStatusBadge';
import { Invoice } from '@/types/models';
import { getInvoiceLineItems } from '@/services/invoiceService';
import ProfessionalInvoiceView from '../ProfessionalInvoiceView';

interface FlatInvoicesProps {
  invoices: Invoice[];
  onEditInvoice: (invoice: Invoice) => void;
  onDeleteInvoice: (invoice: Invoice) => void;
  formatAmount: (amountInCents: number) => string;
}

export const FlatInvoices: React.FC<FlatInvoicesProps> = ({
  invoices,
  onEditInvoice,
  onDeleteInvoice,
  formatAmount,
}) => {
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  const handleViewInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Family</TableHead>
              <TableHead>Invoice Number</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Due Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.length > 0 ? (
              invoices.map((invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell>{invoice.family?.name || 'Unknown Family'}</TableCell>
                  <TableCell className="font-medium">{invoice.invoice_number || 'PENDING'}</TableCell>
                  <TableCell>{formatAmount(invoice.total_cents)}</TableCell>
                  <TableCell>{format(new Date(invoice.due_date), 'MMM d, yyyy')}</TableCell>
                  <TableCell>
                    <InvoiceStatusBadge status={invoice.status} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewInvoice(invoice)}
                      >
                        View Details
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => onEditInvoice(invoice)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => onDeleteInvoice(invoice)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-6 text-muted-foreground">
                  No invoices found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
      <ProfessionalInvoiceView
        open={!!selectedInvoice}
        onOpenChange={() => setSelectedInvoice(null)}
        invoice={selectedInvoice}
        organizationId={selectedInvoice?.organization_id || ''}
      />
    </Card>
  );
};
